import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Bookmark, Plus } from "lucide-react";
import { useApi } from "@/lib/api";
import { SOURCE_META, SOURCE_ORDER } from "@/lib/sourceMeta";
import MasonryGrid from "@/components/MasonryGrid";
import EntryCard from "@/components/EntryCard";
import { useCapture } from "@/components/CaptureContext";

export default function Brain() {
  const api = useApi();
  const { openCapture } = useCapture();
  const [searchParams] = useSearchParams();
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [tag, setTag] = useState(searchParams.get("tag") || "");
  const [savedOnly, setSavedOnly] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQ(input), 350);
    return () => clearTimeout(t);
  }, [input]);

  const { data: tagStats } = useQuery({ queryKey: ["tags"], queryFn: () => api.get("/tags") });

  const params = new URLSearchParams({ type: "brain", limit: "60" });
  if (q) params.set("q", q);
  if (sourceType) params.set("source_type", sourceType);
  if (tag) params.set("tag", tag);
  if (savedOnly) params.set("saved", "true");

  const { data: entries, isLoading } = useQuery({
    queryKey: ["entries", { type: "brain", q, sourceType, tag, savedOnly }],
    queryFn: () => api.get(`/entries?${params.toString()}`),
  });

  const chip = (active) =>
    `shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium border transition-[background-color,border-color,color] ${
      active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-stone-400"
    }`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 data-testid="brain-title" className="font-serif text-3xl md:text-4xl tracking-tight">
          Brain archive
        </h1>
        {entries && (
          <span data-testid="brain-count" className="text-xs font-medium text-stone-400">
            {entries.length} {entries.length === 1 ? "item" : "items"}
          </span>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
        <input
          data-testid="brain-search-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search your archive — titles, summaries, notes, tags…"
          className="w-full rounded-full border border-border bg-card py-3 pl-11 pr-4 text-sm outline-none focus:border-primary/60 transition-[border-color]"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
        <button data-testid="filter-source-all" onClick={() => setSourceType("")} className={chip(!sourceType)}>
          All
        </button>
        {SOURCE_ORDER.map((s) => {
          const Icon = SOURCE_META[s].icon;
          return (
            <button
              key={s}
              data-testid={`filter-source-${s}`}
              onClick={() => setSourceType(sourceType === s ? "" : s)}
              className={chip(sourceType === s)}
            >
              <span className="inline-flex items-center gap-1.5">
                <Icon className="h-3.5 w-3.5" /> {SOURCE_META[s].label}
              </span>
            </button>
          );
        })}
        <button
          data-testid="filter-saved"
          onClick={() => setSavedOnly(!savedOnly)}
          className={chip(savedOnly)}
        >
          <span className="inline-flex items-center gap-1.5">
            <Bookmark className={`h-3.5 w-3.5 ${savedOnly ? "fill-current" : ""}`} /> Saved
          </span>
        </button>
      </div>

      {!!tagStats?.length && !savedOnly && (
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
          {tagStats.slice(0, 8).map(({ tag: t, count }) => (
            <button
              key={t}
              data-testid={`filter-topic-${t}`}
              onClick={() => setTag(tag === t ? "" : t)}
              className={chip(tag === t)}
            >
              #{t} <span className="opacity-60">{count}</span>
            </button>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="break-inside-avoid mb-6 rounded-2xl bg-card border border-border p-4 animate-pulse">
              <div className="h-40 bg-secondary rounded-xl" />
              <div className="h-4 mt-3 bg-secondary rounded w-3/4" />
              <div className="h-3 mt-2 bg-secondary rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : entries?.length ? (
        <MasonryGrid>
          {entries.map((e) => (
            <EntryCard key={e.id} entry={e} />
          ))}
        </MasonryGrid>
      ) : (
        <div data-testid="brain-empty-state" className="rounded-3xl border border-dashed border-border bg-card p-14 text-center">
          <p className="text-sm text-muted-foreground">
            {q || sourceType || tag || savedOnly
              ? "Nothing matches this filter."
              : "Your Brain archive is empty — capture the first thing you're curious about."}
          </p>
          <button
            data-testid="brain-empty-capture-button"
            onClick={openCapture}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            <Plus className="h-4 w-4" /> Capture something
          </button>
        </div>
      )}
    </div>
  );
}
